import Carousel from '@allesauto/core/carousel';
import { GET_ACCESSORY_BY_ID, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { useAccessoriesTop } from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts.ts';
import { getAccessoryCarouselVehicleData } from '@allesauto/utils/funcs.ts';
import { useLazyQuery } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import AccessoryDealerPriceOverlay from '@/components/PDP/aboveFold/accessoryDetailsFooter/AccessoryDealerPriceOverlay';

import { useAddAccessoryToCart } from '@/features/Accessories/hooks';
import { useAddToCart, useUpdateCart } from '@/global/hooks/shoppingCart';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';

import AccessoriesTopRatedItem from './AccessoriesTopRatedItem';
import AccessoriesCatalog from '../Catalog/AccessoriesCatalog';
import AccessoriesDetails from '../Details/AccessoriesDetails';

export interface AccessoriesTopRatedProps {
  vehicleCartItem?: SchemaTypes.CartItem;
  vehicleGarageItem?: SchemaTypes.GarageVehicle;
  vehiclePdpItem?: SchemaTypes.Vehicle;
  currentSelectedCar?: number;
}

const AccessoriesTopRated: FC<
  React.PropsWithChildren<AccessoriesTopRatedProps>
> = ({
  vehicleCartItem,
  vehicleGarageItem,
  currentSelectedCar,
  vehiclePdpItem,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { addToCart } = useAddToCart();
  const { updateCart } = useUpdateCart();
  const [isCarouselMoving, setIsCarouselMoving] = useState(false);
  const [selectedShipment, setSelectedShipment] =
    useState<SchemaTypes.Shipment>();

  const {
    accessoriesDealerProviderId,
    vehicleVin,
    vehicleSeries,
    makeName,
    dealer,
  } = getAccessoryCarouselVehicleData({
    vehicleCartItem,
    vehicleGarageItem,
    vehiclePdpItem,
  });

  const { result } = useAccessoriesTop(
    {
      dealerId: accessoriesDealerProviderId,
      vin: vehicleVin,
      series: vehicleSeries,
    },
    {
      skip: !accessoriesDealerProviderId,
    },
    vehicleGarageItem && currentSelectedCar
  );

  const addAccessoryToCart = useAddAccessoryToCart(
    addToCart,
    updateCart,
    makeName
  );

  const [queryAccessoryById] = useLazyQuery(GET_ACCESSORY_BY_ID, {
    onCompleted: (returnedData) => {
      if (returnedData && returnedData.accessoryById.accessory) {
        dispatch(
          openOverlay({
            children: (
              <AccessoryDealerPriceOverlay
                garageOverlay
                accessory={returnedData.accessoryById.accessory}
                searchLocation={undefined}
                dealer={dealer}
                shipment={selectedShipment}
                step={1}
                onSubmit={(
                  _submittedSearchLocation,
                  submittedSelectedDealer,
                  _submittedDealerPrice,
                  submittedDealerProductId,
                  submittedShipment
                ) => {
                  setSelectedShipment(submittedShipment);

                  addAccessoryToCart(
                    submittedSelectedDealer,
                    submittedDealerProductId,
                    submittedShipment
                  );

                  import('@allesauto/core/toastify').then((module) => {
                    module.toastifyFactory(
                      ToastifyType.SUCCESS,
                      translate(
                        intl,
                        'ui.SHOPPING_CART.ACCESSORIES.ADDED_TO_CART_TOAST.HEADER'
                      ),
                      translate(
                        intl,
                        'ui.SHOPPING_CART.ACCESSORIES.ADDED_TO_CART_TOAST.TEXT',
                        { title: returnedData.accessoryById.title }
                      )
                    );
                  });

                  dispatch(closeOverlay());
                }}
              />
            ),
            title: translate(
              intl,
              'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.OVERLAY_TITLE'
            ),
          })
        );
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('Query Error:', err);
    },
  });

  const handleCarouselMoving = (movingState: boolean) => {
    setIsCarouselMoving(movingState);
  };

  const showCatalogOverlay = useCallback(
    () =>
      dispatch(
        openOverlay({
          title: translate(
            intl,
            'ui.SHOPPING_CART.ACCESSORIES.OVERLAY_CATALOG_TITLE',
            { makeName: makeName ?? '' }
          ),
          children: (
            <AccessoriesCatalog
              accessoriesDealerProviderId={accessoriesDealerProviderId}
              vehicleVin={vehicleVin}
              vehicleSeries={vehicleSeries}
            />
          ),
        })
      ),
    [
      accessoriesDealerProviderId,
      dispatch,
      intl,
      makeName,
      vehicleSeries,
      vehicleVin,
    ]
  );

  const showDealerPriceOverlay = useCallback(
    (productId: string, accessoriesDealerProviderId: string | undefined) => {
      queryAccessoryById({
        variables: {
          filter: {
            dealerId: accessoriesDealerProviderId,
          },
          accessoryId: productId,
        },
      });
    },
    [queryAccessoryById]
  );

  const showDetailsOverlay = useCallback(
    (productId: string) =>
      dispatch(
        openOverlay({
          title: translate(
            intl,
            'ui.SHOPPING_CART.ACCESSORIES.OVERLAY_DETAILS_TITLE'
          ),
          children: (
            <AccessoriesDetails
              accessoriesDealerProviderId={accessoriesDealerProviderId}
              productId={productId}
              showDealerPriceOverlay={showDealerPriceOverlay}
            />
          ),
        })
      ),
    [accessoriesDealerProviderId, dispatch, intl, showDealerPriceOverlay]
  );

  if (!result || !result.length) {
    return null;
  }

  return (
    <Carousel
      itemsLDesktop={4}
      itemsTable={3}
      itemsToScrollTablet={3}
      itemsToScrollLDesktop={4}
      headlineTag={4}
      headline={translate(intl, 'ui.SHOPPING_CART.ACCESSORIES.TITLE')}
      textLink1Props={{
        text: translate(intl, 'ui.SHOPPING_CART.ACCESSORIES.SHOW_ALL_LINK'),
        onClick: showCatalogOverlay,
        customClassName: 'light-grey-background',
      }}
      isInfinite={false}
      handleAfterChange={handleCarouselMoving}
      handleBeforeChange={handleCarouselMoving}
      classNames={{
        sectionClass: '!bg-grey10 !p-0 !-mt-15px md:!-mt-20px',
        containerClass: '!col-end-24 !col-start-2 !pb-40px lg:!pb-[110px]',
        itemClass: '!px-0',
        buttonLeftClass: 'md:!col-start-1 left-15px md:left-20px',
        buttonRightClass: 'md:!col-start-24 right-15px md:right-20px',
        dotListClass: '!pb-0 lg:!pb-60px !px-15px lg:!px-0',
      }}
    >
      {result.map((product) => (
        <AccessoriesTopRatedItem
          key={product.productId}
          product={product}
          allowClick={!isCarouselMoving}
          onClick={(productId) => showDetailsOverlay(productId)}
        />
      ))}
    </Carousel>
  );
};

export default AccessoriesTopRated;
